import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { AdBanner, useShowAds } from '../components/AdBanner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { RamadanTopBanner, RamadanPromoBadge, CheckoutCharityMessage } from '../components/PromoBanner';
import { 
    Check, 
    Crown, 
    Star, 
    Loader2,
    CheckCircle,
    XCircle,
    Tag,
    Percent,
    X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubscriptionPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, token, isPremium, refreshUser } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const showAds = useShowAds(isPremium);
    
    // Discount code state - now supports multiple codes
    const [discountCode, setDiscountCode] = useState('');
    const [appliedDiscounts, setAppliedDiscounts] = useState([]); // Array of applied discount codes
    const [validatingCode, setValidatingCode] = useState(false);
    const [codeError, setCodeError] = useState(null);

    // Calculate total discount (capped at 100%)
    const totalDiscount = Math.min(100, appliedDiscounts.reduce((sum, d) => sum + d.discount_percent, 0));
    const isFreeSubscription = totalDiscount >= 100;

    const fetchPlans = async () => {
        try {
            const response = await axios.get(`${API}/subscriptions/plans`);
            setPlans(response.data);
        } catch (error) {
            toast.error('Failed to load subscription plans');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
        
        // Check for payment status
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            pollPaymentStatus(sessionId);
        }
    }, [searchParams]);

    const validateDiscountCode = async () => {
        if (!discountCode.trim()) return;
        
        const codeUpper = discountCode.trim().toUpperCase();
        
        // Check if code is already applied
        if (appliedDiscounts.some(d => d.code === codeUpper)) {
            setCodeError('This code is already applied');
            toast.error('Code already applied', {
                description: 'You cannot use the same discount code twice.'
            });
            return;
        }
        
        // Check if we're already at 100% discount
        if (totalDiscount >= 100) {
            setCodeError('Maximum discount reached');
            toast.error('Maximum discount reached', {
                description: 'You already have 100% off!'
            });
            return;
        }
        
        setValidatingCode(true);
        setCodeError(null);
        
        try {
            const response = await axios.post(`${API}/discount-codes/validate`, {
                code: codeUpper
            });
            
            if (response.data.valid) {
                // Add to applied discounts
                setAppliedDiscounts(prev => [...prev, {
                    code: response.data.code,
                    discount_percent: response.data.discount_percent
                }]);
                setDiscountCode(''); // Clear input
                
                const newTotal = Math.min(100, totalDiscount + response.data.discount_percent);
                toast.success(`Discount code applied!`, {
                    description: newTotal >= 100 
                        ? '100% off - Your subscription will be free!' 
                        : `${response.data.discount_percent}% off added. Total discount: ${newTotal}%`
                });
            }
        } catch (error) {
            setCodeError(error.response?.data?.detail || 'Invalid code');
            toast.error('Invalid discount code', {
                description: error.response?.data?.detail || 'Please check the code and try again.'
            });
        } finally {
            setValidatingCode(false);
        }
    };

    const removeDiscount = (codeToRemove) => {
        setAppliedDiscounts(prev => prev.filter(d => d.code !== codeToRemove));
        toast.info('Discount removed');
    };

    const clearAllDiscounts = () => {
        setAppliedDiscounts([]);
        setDiscountCode('');
        setCodeError(null);
    };

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 5;
        
        if (attempts >= maxAttempts) {
            setPaymentStatus('timeout');
            return;
        }

        try {
            const response = await axios.get(`${API}/payments/status/${sessionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.payment_status === 'paid') {
                setPaymentStatus('success');
                await refreshUser();
                toast.success('Payment successful!', {
                    description: 'Welcome to Ustadh Mode! Your subscription is now active.'
                });
                return;
            } else if (response.data.status === 'expired') {
                setPaymentStatus('expired');
                return;
            }

            // Continue polling
            setPaymentStatus('processing');
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
        } catch (error) {
            console.error('Error checking payment status:', error);
            setPaymentStatus('error');
        }
    };

    const handleSubscribe = async (planId) => {
        if (planId === 'free') {
            navigate('/chat');
            return;
        }

        setCheckoutLoading(planId);
        
        try {
            const checkoutData = {
                plan_id: planId,
                origin_url: window.location.origin
            };
            
            // Include all applied discount codes
            if (appliedDiscounts.length > 0) {
                checkoutData.discount_codes = appliedDiscounts.map(d => d.code);
            }
            
            const response = await axios.post(`${API}/payments/checkout`, checkoutData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Redirect to Stripe checkout (or handle free subscription)
            if (response.data.free_subscription) {
                toast.success('Subscription activated!', {
                    description: 'Your 100% discount has been applied. Enjoy Ustadh Mode!'
                });
                await refreshUser();
                navigate('/chat');
            } else {
                window.location.href = response.data.url;
            }
        } catch (error) {
            toast.error('Failed to create checkout session', {
                description: error.response?.data?.detail || 'Please try again'
            });
            setCheckoutLoading(null);
        }
    };

    const getPlanDetails = (plan) => {
        const isPopular = plan.id === 'ustadh_monthly';
        const isCurrentPlan = user?.subscription_status === plan.id;
        
        return {
            ...plan,
            isPopular,
            isCurrentPlan,
            ctaText: isCurrentPlan 
                ? 'Current Plan' 
                : plan.price === 0 
                    ? 'Get Started' 
                    : 'Subscribe Now'
        };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Navbar />
                <div className="pt-24 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Promo Banner - Uncomment when running promotions */}
            {/* {!isPremium && <RamadanTopBanner />} */}
            
            <Navbar />
            
            <div className="pt-24 pb-16 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Payment Status Banner */}
                    {paymentStatus && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-8"
                        >
                            {paymentStatus === 'success' && (
                                <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/10 border border-primary/20">
                                    <CheckCircle className="w-6 h-6 text-primary" />
                                    <div>
                                        <p className="font-semibold text-primary">Payment Successful!</p>
                                        <p className="text-sm text-muted-foreground">Your subscription is now active. Enjoy Ustadh Mode!</p>
                                    </div>
                                    <Button onClick={() => navigate('/ustadh')} className="ml-auto bg-primary">
                                        Go to Ustadh Mode
                                    </Button>
                                </div>
                            )}
                            {paymentStatus === 'processing' && (
                                <div className="flex items-center gap-3 p-4 rounded-lg bg-secondary/10 border border-secondary/20">
                                    <Loader2 className="w-6 h-6 text-secondary animate-spin" />
                                    <div>
                                        <p className="font-semibold text-secondary">Processing Payment...</p>
                                        <p className="text-sm text-muted-foreground">Please wait while we confirm your payment.</p>
                                    </div>
                                </div>
                            )}
                            {(paymentStatus === 'expired' || paymentStatus === 'error' || paymentStatus === 'timeout') && (
                                <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                                    <XCircle className="w-6 h-6 text-destructive" />
                                    <div>
                                        <p className="font-semibold text-destructive">Payment Issue</p>
                                        <p className="text-sm text-muted-foreground">
                                            {paymentStatus === 'expired' 
                                                ? 'Your payment session expired. Please try again.'
                                                : 'There was an issue processing your payment. Please try again.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-12"
                    >
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-medium rounded-full bg-secondary/10 text-secondary border border-secondary/20">
                            <Crown className="w-4 h-4" />
                            Subscription Plans
                        </span>
                        <h1 className="font-heading text-4xl md:text-5xl font-semibold text-foreground mb-4">
                            Choose Your Learning Path
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            {isPremium 
                                ? 'You currently have access to Ustadh Mode. Manage your subscription below.'
                                : 'Upgrade to Ustadh Mode for voice-enabled Quran teaching, tajweed feedback, and personalized lesson plans.'}
                        </p>
                    </motion.div>

                    {/* Pricing Cards */}
                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {plans.map((plan, index) => {
                            const details = getPlanDetails(plan);
                            
                            return (
                                <motion.div
                                    key={plan.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="relative"
                                >
                                    {details.isPopular && (
                                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                                            <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-secondary text-secondary-foreground">
                                                <Star className="w-3 h-3" />
                                                Most Popular
                                            </span>
                                        </div>
                                    )}
                                    <Card className={`h-full ${details.isPopular ? 'border-secondary tracing-beam' : 'border-border'} ${details.isCurrentPlan ? 'ring-2 ring-primary' : ''}`}>
                                        <CardHeader>
                                            <CardTitle className="font-heading text-xl">{plan.name}</CardTitle>
                                            <CardDescription>
                                                <span className="text-4xl font-bold text-foreground">${plan.price}</span>
                                                <span className="text-muted-foreground">
                                                    {plan.price === 0 ? ' forever' : plan.id.includes('yearly') ? '/year' : '/month'}
                                                </span>
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <ul className="space-y-3 mb-6">
                                                {plan.features.map((feature, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm">
                                                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${details.isPopular ? 'text-secondary' : 'text-primary'}`} />
                                                        <span className="text-foreground">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            
                                            <Button 
                                                className={`w-full ${
                                                    details.isCurrentPlan 
                                                        ? 'bg-muted text-muted-foreground cursor-default'
                                                        : details.isPopular 
                                                            ? 'bg-secondary hover:bg-secondary/90 text-secondary-foreground' 
                                                            : ''
                                                }`}
                                                variant={details.isCurrentPlan ? 'outline' : details.isPopular ? 'default' : 'outline'}
                                                onClick={() => !details.isCurrentPlan && handleSubscribe(plan.id)}
                                                disabled={details.isCurrentPlan || checkoutLoading === plan.id}
                                                data-testid={`subscribe-${plan.id}`}
                                            >
                                                {checkoutLoading === plan.id ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Processing...
                                                    </>
                                                ) : details.isCurrentPlan ? (
                                                    <>
                                                        <CheckCircle className="w-4 h-4 mr-2" />
                                                        Current Plan
                                                    </>
                                                ) : (
                                                    details.ctaText
                                                )}
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Discount Code Section */}
                    {!isPremium && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="mt-10 max-w-md mx-auto"
                        >
                            <Card className="border-border bg-card/50">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <Tag className="w-5 h-5 text-secondary" />
                                            <h3 className="font-semibold text-foreground">Discount Codes</h3>
                                        </div>
                                        {totalDiscount > 0 && (
                                            <span className={`text-sm font-bold ${isFreeSubscription ? 'text-green-500' : 'text-secondary'}`}>
                                                {isFreeSubscription ? 'FREE!' : `${totalDiscount}% off`}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Applied Discounts List */}
                                    {appliedDiscounts.length > 0 && (
                                        <div className="space-y-2 mb-4">
                                            {appliedDiscounts.map((discount) => (
                                                <div key={discount.code} className="flex items-center justify-between p-2 rounded-lg bg-primary/10 border border-primary/20">
                                                    <div className="flex items-center gap-2">
                                                        <Percent className="w-4 h-4 text-primary" />
                                                        <span className="font-medium text-primary">{discount.code}</span>
                                                        <span className="text-sm text-muted-foreground">({discount.discount_percent}% off)</span>
                                                    </div>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => removeDiscount(discount.code)}
                                                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                            
                                            {/* Total discount summary */}
                                            {appliedDiscounts.length > 1 && (
                                                <div className="text-sm text-muted-foreground text-center pt-1">
                                                    Combined: {totalDiscount}% off
                                                    {totalDiscount >= 100 && ' (capped at 100%)'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* Add more codes input (unless at 100%) */}
                                    {totalDiscount < 100 && (
                                        <div className="flex gap-2">
                                            <Input
                                                placeholder={appliedDiscounts.length > 0 ? "Add another code" : "Enter code"}
                                                value={discountCode}
                                                onChange={(e) => {
                                                    setDiscountCode(e.target.value.toUpperCase());
                                                    setCodeError(null);
                                                }}
                                                onKeyDown={(e) => e.key === 'Enter' && validateDiscountCode()}
                                                className={`uppercase ${codeError ? 'border-destructive' : ''}`}
                                                data-testid="discount-code-input"
                                            />
                                            <Button 
                                                onClick={validateDiscountCode}
                                                disabled={!discountCode.trim() || validatingCode}
                                                variant="outline"
                                                data-testid="apply-discount-btn"
                                            >
                                                {validatingCode ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    'Apply'
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                    
                                    {/* 100% discount message */}
                                    {isFreeSubscription && (
                                        <div className="text-center p-3 rounded-lg bg-green-500/10 border border-green-500/20 mt-3">
                                            <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                                            <p className="text-sm font-medium text-green-500">100% discount applied!</p>
                                            <p className="text-xs text-muted-foreground">Your subscription will be free</p>
                                        </div>
                                    )}
                                    
                                    {codeError && (
                                        <p className="text-sm text-destructive mt-2 flex items-center gap-1">
                                            <XCircle className="w-4 h-4" />
                                            {codeError}
                                        </p>
                                    )}
                                    
                                    {/* Charity Message */}
                                    <CheckoutCharityMessage />
                                </CardContent>
                            </Card>
                            
                            {/* Promo Badge - Uncomment when running promotions */}
                            {/* <RamadanPromoBadge /> */}
                        </motion.div>
                    )}

                    {/* FAQ or Additional Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="mt-16 text-center"
                    >
                        <p className="text-muted-foreground">
                            Questions about subscriptions?{' '}
                            <a href="mailto:support@aalim.ai" className="text-primary hover:underline">
                                Contact us
                            </a>
                        </p>
                    </motion.div>

                    {/* Inline Ad for Free Users */}
                    {showAds && (
                        <div className="mt-12 max-w-3xl mx-auto">
                            <AdBanner placement="inline" />
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Banner Ad for Free Users */}
            {showAds && <AdBanner placement="banner" />}
        </div>
    );
}
